import React, { useState } from 'react';
import { ExpressCheckoutElement as StripeExpressCheckoutElement } from '@stripe/react-stripe-js';
import { isFuture } from 'date-fns';
import useCheckoutState, { checkoutStates } from '../../hooks/useCheckoutState';
import useCartCost from '../../hooks/useCartCost';
import useCheckoutValidation from '../../hooks/useCheckoutValidation';
import useCheckoutHelpers from '../../hooks/useCheckoutHelpers';
import useRecaptcha from '../../hooks/useRecaptcha';
import { PAGE_TYPES } from '../../enums/page-types';
import getSellerDomain from '../../lib/getSellerDomain';

// Helper function to convert weekly intervals to daily for Apple Pay compatibility
const convertIntervalForApplePay = (interval, intervalCount) => {
  if (interval === 'week') {
    return {
      intervalUnit: 'day',
      intervalCount: intervalCount * 7,
    };
  }
  return {
    intervalUnit: interval,
    intervalCount: intervalCount,
  };
};

const ExpressCheckoutElement = ({ mode = 'express', onReady = () => {} }) => {
  const {
    formRef,
    checkout,
    product,
    setState,
    setFormError,
    setPaymentMethod,
    seller,
    payWhatYouWantPrice,
  } = useCheckoutState();
  const { totalDueAmount, recurringTotalAmount } = useCartCost();
  const { validateMinimumChargeAmount, validateHasStock, validateVariants } =
    useCheckoutValidation();
  const { createPayment, createSubscription } = useCheckoutHelpers();
  const { executeRecaptcha } = useRecaptcha();
  const [isReady, setIsReady] = useState(false);

  const handleExpressCheckoutClick = async event => {
    try {
      // Validate form
      if (mode !== 'express') {
        // Force form validation for non-express mode
        if (formRef.current && !formRef.current.reportValidity()) {
          return;
        }
      }

      // Validate purchase
      validateVariants();
      validateMinimumChargeAmount(payWhatYouWantPrice, product.currency, checkout.locale);
      validateHasStock();

      setPaymentMethod('expressCheckout');

      const options = {};
      // Update payment information with current total
      if (checkout.type === PAGE_TYPES.CHECKOUT) {
        options.paymentDetails = {
          total: {
            label: product.title,
            amount: totalDueAmount,
          },
        };
      } else if (checkout.type === PAGE_TYPES.EVENT) {
        options.paymentDetails = {
          total: {
            label: checkout.title,
            amount: totalDueAmount,
          },
        };
      }

      // Resolve the click event with the options
      event.resolve(options);
    } catch (error) {
      setFormError(error.message);
      setState(checkoutStates.INITIAL);
    }
  };

  const handleExpressCheckoutConfirm = async event => {
    try {
      // Get a fresh reCAPTCHA token
      const recaptchaToken = await executeRecaptcha('checkout');

      if (product.type === 'subscription') {
        await createSubscription({ recaptchaToken, stripePaymentMethodEvent: event });
      } else {
        await createPayment({ recaptchaToken, stripePaymentMethodEvent: event });
      }
    } catch (error) {
      setFormError(error.message);
      setState(checkoutStates.INITIAL);
    }
  };

  let recurringPaymentRequest = null;
  if (product.type === 'subscription') {
    function getPaymentPlanEndDate() {
      const startDate = product.startDate
        ? new Date(product.startDate)
        : product.trialPeriodDays
          ? new Date(new Date().getTime() + product.trialPeriodDays * 24 * 60 * 60 * 1000)
          : new Date();

      const endDate = new Date(startDate);

      switch (product.interval) {
        case 'day':
          endDate.setDate(endDate.getDate() + product.intervalCount * product.planIterations);
          break;
        case 'week':
          endDate.setDate(endDate.getDate() + product.intervalCount * product.planIterations * 7);
          break;
        case 'month':
          endDate.setMonth(endDate.getMonth() + product.intervalCount * product.planIterations);
          break;
        case 'year':
          endDate.setFullYear(
            endDate.getFullYear() + product.intervalCount * product.planIterations,
          );
          break;
        default:
          return startDate;
      }

      return endDate;
    }

    function getTrialEndDate() {
      return new Date(new Date().getTime() + product.trialPeriodDays * 24 * 60 * 60 * 1000);
    }

    function getRecurringPaymentStartDate() {
      if (product.startDate && isFuture(new Date(product.startDate))) {
        return new Date(product.startDate);
      }

      if (product.trialPeriodDays) {
        return new Date(new Date().getTime() + product.trialPeriodDays * 24 * 60 * 60 * 1000);
      }

      return new Date();
    }

    // Convert interval for Apple Pay compatibility
    const { intervalUnit, intervalCount } = convertIntervalForApplePay(
      product.interval,
      product.intervalCount,
    );

    recurringPaymentRequest = {
      paymentDescription: product.title,
      regularBilling: {
        amount: recurringTotalAmount,
        label: product.title,
        recurringPaymentStartDate: getRecurringPaymentStartDate(),
        ...(product.planIterations && {
          recurringPaymentEndDate: getPaymentPlanEndDate(),
        }),
        recurringPaymentIntervalUnit: intervalUnit,
        recurringPaymentIntervalCount: intervalCount,
      },
      ...(product.trialPeriodDays && {
        trialBilling: {
          amount: 0,
          label: product.title,
          recurringPaymentStartDate: new Date(),
          recurringPaymentEndDate: getTrialEndDate(),
          recurringPaymentIntervalUnit: 'day',
          recurringPaymentIntervalCount: product.trialPeriodDays,
        },
      }),
      managementURL: `${getSellerDomain(seller)}/portal`,
    };
  }

  return (
    <div
      className="cp_stripe-express-checkout-element"
      style={{ display: isReady ? 'block' : 'none' }}
    >
      <StripeExpressCheckoutElement
        options={{
          business: {
            name: seller.displayName,
          },
          emailRequired: true,
          billingAddressRequired: true,
          buttonType: {
            applePay: 'plain',
            googlePay: 'plain',
          },
          buttonTheme: {
            applePay: checkout.buttonBackgroundColor === 'white' ? 'white' : 'black',
            googlePay: checkout.buttonBackgroundColor === 'white' ? 'white' : 'black',
            paypal: checkout.buttonBackgroundColor === 'white' ? 'white' : 'black',
          },
          layout: {
            overflow: 'never',
          },
          paymentMethods: {
            applePay: 'always',
            googlePay: 'always',
            link: 'auto',
            paypal: 'auto',
            amazonPay: 'auto',
          },
          ...(recurringPaymentRequest && {
            applePay: {
              recurringPaymentRequest,
            },
          }),
        }}
        onClick={handleExpressCheckoutClick}
        onConfirm={handleExpressCheckoutConfirm}
        onCancel={() => setPaymentMethod('card')}
        onReady={event => {
          if (!event.availablePaymentMethods) {
            setIsReady(false);
          } else {
            setIsReady(true);
            onReady();
          }
        }}
      />

      {mode === 'default' && (
        <div className="cp_pay-with-card-wrapper flex overflow-hidden relative items-center py-2 my-4">
          <hr className="cp_pay-with-card-divider bg-gray-100 h-px w-full border-none" />
          <p className="cp_pay-with-card-text bg-white absolute -translate-x-1/2 left-1/2 whitespace-nowrap px-2 text-center text-gray-400 text-sm">
            {checkout.orPayWithCardText}
          </p>
        </div>
      )}
    </div>
  );
};

export default ExpressCheckoutElement;
